'use client'

import { AlignJustify, ChevronLeft, ExternalLink } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { memo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'

interface HamburgerMenuProps {
  logo: string
  unityId?: string
}

interface MenuLinkData {
  href: string
  label: string
  external?: boolean
}

interface MenuLinkProps {
  href: string
  children: string
  external?: boolean
  onLinkClick?: () => void
}

interface MenuSectionProps {
  links: MenuLinkData[]
  title?: string
  onLinkClick?: () => void
}

const MENU_LINKS = {
  main: [
    { href: '/diamond', label: 'SóEducador Diamond' },
    { href: '/app', label: 'Matrículas' },
    { href: '/app/meus-certificados', label: 'Certificados' },
    { href: '/app/historico-pedidos', label: 'Pagamentos' },
  ],
  secondary: [
    { href: '/certificado/sobre', label: 'Certificados' },
    { href: '/parceria-mec', label: 'e-MEC' },
  ],
  tertiary: [
    { href: '/cursos', label: 'Nossos Cursos' },
    { href: '/quem-somos', label: 'Sobre Nós' },
    { href: '/ajuda', label: 'Ajuda' },
  ],
} satisfies Record<string, MenuLinkData[]>

const MenuLink = memo(function MenuLinkComponent({
  href,
  children,
  external = false,
  onLinkClick,
}: MenuLinkProps) {
  const handleClick = () => {
    onLinkClick?.()
  }

  return (
    <Link
      className="group flex items-center gap-2 rounded-lg px-3 py-2 font-medium text-sm transition-all duration-200 hover:bg-primary-50 hover:text-primary-700 focus:bg-primary-50 focus:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      href={href}
      onClick={handleClick}
      rel={external ? 'noopener noreferrer' : undefined}
      target={external ? '_blank' : undefined}
    >
      <span>{children}</span>
      {external && (
        <ExternalLink className="size-3 opacity-60 transition-opacity group-hover:opacity-100" />
      )}
    </Link>
  )
})

const MenuSection = memo(function MenuSectionComponent({
  links,
  title,
  onLinkClick,
}: MenuSectionProps) {
  return (
    <div className="space-y-1">
      {title && (
        <h3 className="px-3 py-2 font-semibold text-gray-500 text-xs uppercase tracking-wider">
          {title}
        </h3>
      )}

      <div className="space-y-0.5">
        {links.map(link => (
          <MenuLink
            external={link.external}
            href={link.href}
            key={link.href}
            onLinkClick={onLinkClick}
          >
            {link.label}
          </MenuLink>
        ))}
      </div>
    </div>
  )
})

const DialogCloseButton = memo(function DialogCloseButtonComponent() {
  return (
    <DialogClose asChild>
      <Button
        aria-label="Fechar menu"
        className="flex items-center gap-2 rounded-lg px-4 py-2 font-medium text-sm transition-all duration-200 hover:bg-primary-100 focus:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        variant="outline"
      >
        <ChevronLeft className="size-4" />
        <span>Voltar</span>
      </Button>
    </DialogClose>
  )
})

const DialogLogo = memo(function DialogLogoComponent({
  logo,
  onLinkClick,
}: {
  logo: string
  onLinkClick?: () => void
}) {
  const handleClick = () => {
    onLinkClick?.()
  }

  return (
    <Link
      aria-label="Ir para página inicial"
      className="block rounded-lg transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      href="/"
      onClick={handleClick}
    >
      <Image
        alt="Logo Zamply"
        className="h-auto w-auto"
        height={38}
        priority
        quality={100}
        src={logo}
        width={280.67}
      />
    </Link>
  )
})

const MainActionButtons = memo(function MainActionButtonsComponent({
  onLinkClick,
  unityId,
}: {
  onLinkClick?: () => void
  unityId?: string
}) {
  const handleClick = () => {
    onLinkClick?.()
  }

  const showPostgraduate = !unityId || unityId !== 'U08E5DDFF'

  return (
    <div
      className={`grid gap-3 ${showPostgraduate ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}
    >
      <Button
        asChild
        className="h-12 rounded-xl font-semibold text-base transition-all duration-200 hover:shadow-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        size="lg"
      >
        <Link href="/cursos" onClick={handleClick}>
          Todos os Cursos
        </Link>
      </Button>
      {showPostgraduate && (
        <Button
          asChild
          className="h-12 rounded-xl font-semibold text-base transition-all duration-200 hover:shadow-md focus:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          size="lg"
          variant="outline"
        >
          <Link href="/cursos/pos-graduacoes" onClick={handleClick}>
            Pós-Graduação
          </Link>
        </Button>
      )}
    </div>
  )
})

export const HamburgerMenu = memo(function HamburgerMenuComponent({
  logo,
  unityId,
}: HamburgerMenuProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handleLinkClick = () => {
    setIsOpen(false)
  }

  const hideDiamondFor = ['U5071E012', 'UD2A7AE51', 'UE9E55FE9', 'U08E5DDFF']

  const filteredMainLinks = MENU_LINKS.main.filter(link => {
    if (link.href === '/diamond') {
      if (!unityId) {
        return true
      }
      const shouldHide = hideDiamondFor.includes(unityId)
      return !shouldHide
    }
    return true
  })

  const filteredSecondaryLinks = MENU_LINKS.secondary.filter(link => {
    if (!unityId) {
      return true
    }
    if (unityId === 'UE9E55FE9') {
      const shouldHide = ['/certificado/sobre', '/parceria-mec'].includes(
        link.href
      )
      return !shouldHide
    }
    return true
  })

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger asChild>
        <Button
          aria-label="Abrir menu de navegação"
          className="mr-4 transition-all duration-300 hover:bg-primary-600 hover:text-white hover:shadow-lg focus:bg-primary-600 focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 xl:hidden"
          data-unity-id={unityId}
          size="icon"
          variant="outline"
        >
          <AlignJustify className="size-5" />
        </Button>
      </DialogTrigger>

      <DialogContent className="mobile-dialog-content h-full w-full max-w-2xl overflow-y-auto py-6 [&>button]:hidden">
        <DialogHeader className="space-y-4">
          <DialogCloseButton />
        </DialogHeader>

        <div className="flex items-center justify-center py-4">
          <DialogLogo logo={logo} onLinkClick={handleLinkClick} />
        </div>

        <div className="space-y-6">
          <MainActionButtons onLinkClick={handleLinkClick} unityId={unityId} />

          <div className="space-y-4">
            <MenuSection
              links={filteredSecondaryLinks}
              onLinkClick={handleLinkClick}
              title="Informações"
            />
          </div>

          <div className="mt-4 flex items-start justify-between gap-2">
            <MenuSection
              links={filteredMainLinks}
              onLinkClick={handleLinkClick}
              title="Minha Conta"
            />

            <Separator className="h-44 bg-primary-200" orientation="vertical" />

            <MenuSection
              links={MENU_LINKS.tertiary}
              onLinkClick={handleLinkClick}
              title="Navegação"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
})
